import { clientStorage } from "@/lib/clientStorage";
import React, { useState, useRef } from "react";
import { GUIDELINES } from "./guidelineCriteria";
import GuidelineDrawer from "./GuidelineDrawer";
import { Button } from "@/components/ui/button";
import { Upload, Trash2, AlertCircle } from "lucide-react";

const getStoredList = (key) => {
  try {
    const stored = clientStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (e) { return []; }
};

const setStoredList = (key, list) => {
  clientStorage.setItem(key, JSON.stringify(list));
};

function SelectWithAdd({ value, onChange, storageKey, label, defaultOptions = [] }) {
  const [options, setOptions] = useState(() => {
    const stored = getStoredList(storageKey);
    if (stored.length === 0 && defaultOptions.length > 0) {
      setStoredList(storageKey, defaultOptions);
      return defaultOptions;
    }
    return stored;
  });
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState("");

  const handleAdd = () => {
    if (newValue.trim()) {
      const updated = [...options, newValue.trim()];
      const unique = [...new Set(updated)];
      setOptions(unique);
      setStoredList(storageKey, unique);
      onChange(newValue.trim());
      setNewValue("");
      setIsAdding(false);
    }
  };

  if (isAdding) {
    return (
      <div className="flex gap-2 items-center w-full">
        <input
          autoFocus
          type="text"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } else if (e.key === 'Escape') setIsAdding(false); }}
          placeholder={`New ${label.toLowerCase()}...`}
          className="border border-gray-300 dark:border-gray-600 rounded px-2 py-2 text-sm focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400 flex-1 min-w-0"
        />
        <button type="button" onClick={handleAdd} className="text-xs font-semibold text-purple-700 hover:text-purple-800 shrink-0">Save</button>
        <button type="button" onClick={() => setIsAdding(false)} className="text-xs text-gray-500 hover:text-gray-700 shrink-0">Cancel</button>
      </div>
    );
  }

  return (
    <div className="flex gap-2 items-center w-full">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-2 text-sm focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 flex-1 min-w-0 print:appearance-none print:border-none print:border-b print:border-gray-400 print:rounded-none print:px-0 print:py-0"
      >
        <option value="">Select {label}...</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <button 
        type="button" 
        onClick={() => setIsAdding(true)}
        className="shrink-0 w-7 h-7 flex items-center justify-center rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 hover:text-purple-700 hover:border-purple-200 transition-colors print:hidden"
        title={`Add new ${label.toLowerCase()}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
      </button>
    </div>
  );
}

export default function GuidelineSection({ form, onGuidelineChange, onCriteriaChange, onChange, brand = "eas", boeFile, onBoeFileChange }) {
  const [showNotes, setShowNotes] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = (files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) return;
    onBoeFileChange?.(file);
    onChange("boe_name", file.name);
    onChange("boe_size", file.size);
  };

  const removeAttachment = () => {
    onBoeFileChange?.(null);
    onChange("boe_name", "");
    onChange("boe_size", 0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const selected = GUIDELINES[form.guideline];

  return (
    <section>
      <div className="text-xs font-bold tracking-widest text-purple-700 uppercase border-b border-gray-300 dark:border-gray-600 pb-2 mb-6">
        Guideline Selection &amp; Criteria
      </div>

      <div className="mb-8">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Select VLA Guideline
        </label>
        <GuidelineDrawer value={form.guideline} onChange={onGuidelineChange} />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Specific Criteria / Sub-Criteria
        </label>
        <div className="border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 p-4 min-h-[100px]">
          {!selected ? (
            <p className="text-gray-400 italic text-sm text-center mt-4">
              Please select a guideline above to view specific criteria.
            </p>
          ) : (
            <div className="space-y-6">
              {selected.criteria.filter(group => !group.label.toLowerCase().startsWith("documentary")).map((group) => (
                <div key={group.id}>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                    {group.label}
                  </p>
                  <div className="space-y-2">
                    {group.items.map((item) => (
                      <label
                        key={item.id}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={!!form.criteria[item.id]}
                          onChange={(e) => onCriteriaChange(item.id, e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-purple-700 shrink-0"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug group-hover:text-gray-900 dark:group-hover:text-gray-100">
                          {item.text}
                        </span>
                      </label>
                    ))}
                    {group.id.endsWith("_means") && (
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={!!form.waiver_applies}
                          onChange={(e) => onChange("waiver_applies", e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-purple-700 shrink-0"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug group-hover:text-gray-900 dark:group-hover:text-gray-100">
                          Means test waiver applies
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {(showNotes || form.notes) ? (
        <div className="flex flex-col gap-1 mt-6">
          <div className="flex justify-between items-center">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Additional Certification Notes</label>
            <button 
              type="button" 
              onClick={() => { setShowNotes(false); onChange("notes", ""); }} 
              className="text-xs text-gray-500 hover:text-red-600 print:hidden"
            >
              Remove
            </button>
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => onChange("notes", e.target.value)}
            placeholder="Enter any specific compliance notes or merit assessment details..."
            rows={4}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500 resize-none bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
      ) : (
        <div className="mt-6 print:hidden">
          <button 
            type="button" 
            onClick={() => setShowNotes(true)}
            className="text-sm font-semibold text-purple-700 hover:text-purple-800 flex items-center gap-1 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add Additional Certification Notes
          </button>
        </div>
      )}

      {/* Brief of Evidence */}
      <div className="mt-6 border-t border-gray-100 dark:border-gray-800 pt-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Brief of Evidence (BOE)
        </label>
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files)}
          />
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2 bg-white dark:bg-gray-850 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-xs rounded-xl"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 text-gray-500" />
            Upload BOE PDF
          </Button>
          <span className="text-xs text-gray-500">Large PDFs are uploaded to the selected Actionstep matter in chunks.</span>
        </div>

        {(boeFile || form.boe_name) && (
          <div className="mt-3 p-2.5 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between max-w-md print:bg-transparent print:border-none print:p-0 print:mt-1">
            <div className="flex items-center gap-2 overflow-hidden">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full shrink-0 print:hidden" />
              <span className="text-xs font-semibold text-green-800 dark:text-green-300 print:text-black">
                BOE selected: {boeFile?.name || form.boe_name}
                {Number(form.boe_size) > 0 ? ` (${(Number(form.boe_size) / 1024 / 1024).toFixed(1)} MB)` : ""}
              </span>
            </div>
            <button
              type="button"
              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/50 rounded shrink-0 print:hidden"
              onClick={removeAttachment}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
        <div className="flex flex-col gap-1 justify-end">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Lawyer with Carriage</label>
          <SelectWithAdd
            value={form.lawyer}
            onChange={(val) => onChange("lawyer", val)}
            storageKey={brand === "vals" ? "vals_lawyers" : brand === "lacw" ? "lacw_lawyers" : "eas_lawyers"}
            label="Lawyer"
            defaultOptions={brand === "vals" ? [
              "David Elderidge",
              "Brett Cranswick",
              "Greg Johnston",
              "Jamie McConnichie",
              "Patrick Crowle",
              "Joe Oliver",
              "Barnaby Kirk",
              "Angus Morse",
              "Sean Hollis",
              "Tom Wilkie-Black",
              "Charlie Schaffer",
              "Luke Timp",
              "Jack Beeston",
              "Nikola Miskovic",
              "Mindy McLaggan",
              "Jemma Vandersluys",
              "Lucy Franklin",
              "Rose Grant",
              "Christjan Corias",
              "Charlotte Mayberry-Rupert"
            ] : []}
          />
        </div>
        <div className="flex flex-col gap-1 justify-end">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Certified By</label>
          <SelectWithAdd
            value={form.certified_by}
            onChange={(val) => onChange("certified_by", val)}
            storageKey={brand === "vals" ? "vals_certifiers" : brand === "lacw" ? "lacw_certifiers" : "eas_certifiers"}
            label="Certifier"
            defaultOptions={brand === "vals" ? [
              "Patrick C",
              "Greg Johnston",
              "Emma Tinney"
            ] : []}
          />
        </div>
        <div className="flex flex-col gap-1 justify-end">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Date</label>
          <input
            type="date"
            value={form.cert_date}
            onChange={(e) => onChange("cert_date", e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
      </div>

      {form.lawyer && form.certified_by && form.lawyer.trim().toLowerCase() !== form.certified_by.trim().toLowerCase() && (
        <div className="mt-5 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-xl flex gap-3 text-amber-800 dark:text-amber-300 shadow-sm animate-in fade-in slide-in-from-top-1 duration-200 print:hidden">
          <AlertCircle className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="text-xs md:text-sm leading-relaxed">
            <span className="font-bold">Practice Standard Reminder:</span> As per 3.2t of VLA's practice standards, certifiers are reminded that they must (for individual Panel Certifiers) proactively and adequately supervise any work done by others on matters they have certified, ensuring they are aware of obligations and processes that apply to legally assisted clients, and these practice standards.
          </div>
        </div>
      )}

    </section>
  );
}
